import DEFAULT_ROUTES from '../../constants/routes';

export const PROJECT_NAV_COPY = {
  projectNotification: 'Project Notification',
  projectSettings: 'Project Settings',
};

export const PROJECT_SETTINGS_TABS = {
  webhook: 'Webhook configuration',
  deliveryLog: 'Delivery log',
  slackChannelConfig: 'Slack Channel Config',
};

export const SLACK_CHANNEL_CONFIG_COPY = {
  emptyTitle: 'Slack is not connected',
  emptyDescription: 'Connect Slack from App Integration, then configure channels here.',
  loadError: 'Unable to load Slack connection status.',
  heading: 'Slack notifications',
  description: 'Choose one channel and which project events to post.',
  activeOnLabel: 'Active',
  activeOffLabel: 'Inactive',
  channelLabel: 'Channel',
  channelPlaceholder: 'Channel name (e.g. qa-alerts) or ID (e.g. C0123456789)',
  channelHint: 'Select at least one event below to save this channel.',
  eventsLabel: 'Events',
  test: 'Test connection',
  save: 'Save',
  saveSuccess: 'Slack notification settings saved.',
  saveError: 'Unable to save Slack notification settings.',
  testSuccess: 'Test message sent.',
  testError: 'Unable to send test message.',
  channelRequired: 'Enter a Slack channel name or ID.',
  events: {
    project_member_added: 'Project member added',
    project_member_removed: 'Project member removed',
    test_launch_finished: 'Test launch finished',
    webhook_delivery_failed: 'Webhook delivery failed',
  },
};

export const PROJECT_SETTINGS_PAGE_COPY = {
  title: PROJECT_NAV_COPY.projectSettings,
  description: 'Configure the webhook and Slack channels used for project notifications.',
  projectFallback: 'Project',
  accessDenied: 'You do not have access to this page.',
};

export const NOTIFICATION_PANEL_LABELS = {
  heading: 'Notifications',
  empty: 'You are all caught up.',
  loadOlder: 'Load older',
  loading: 'Loading notifications…',
  transportDown: 'Notifications are temporarily unavailable. We will keep trying to reconnect.',
  markRead: 'Mark notification as read',
  markUnread: 'Mark notification as unread',
  unknownTitle: 'Notification',
  close: 'Close notifications',
  markAllRead: 'Mark all read',
  markingAllRead: 'Marking all read…',
};

export const COST_ALERT_LABELS = {
  heading: 'AI credit usage',
  loading: 'Loading AI credit usage…',
  loadError: 'Unable to load AI credit usage.',
  unlimited: 'Your organization has unlimited AI credits.',
  usage: 'AI credits used',
  percentSuffix: 'used',
};

const EVENT_COPY = {
  project_member_added: {
    title: (payload, recipientUserId) => (payload.addedUserId === recipientUserId
      ? "You've been added to a project"
      : 'Project member added'),
    body: (payload, recipientUserId) => (payload.addedUserId === recipientUserId
      ? `You were added to ${payload.projectName || 'a project'} by ${payload.actorName || 'a team member'}.`
      : `${payload.addedUserName || 'A team member'} was added to ${payload.projectName || 'a project'} by ${payload.actorName || 'a team member'}.`),
  },
  project_member_removed: {
    title: (payload, recipientUserId) => (payload.removedUserId === recipientUserId
      ? "You've been removed from a project"
      : 'Project member removed'),
    body: (payload, recipientUserId) => (payload.removedUserId === recipientUserId
      ? `You were removed from ${payload.projectName || 'a project'} by ${payload.actorName || 'a team member'}.`
      : `${payload.removedUserName || 'A team member'} was removed from ${payload.projectName || 'a project'} by ${payload.actorName || 'a team member'}.`),
  },
  test_launch_finished: {
    title: 'Test launch completed',
    body: ({ launchName, projectName, passCount, failCount, abortedCount, skippedCount }) => `${launchName || 'A test launch'} in ${projectName || 'a project'} completed — ${passCount ?? 0} passed, ${failCount ?? 0} failed, ${abortedCount ?? 0} aborted, ${skippedCount ?? 0} skipped.`,
  },
  ai_cost_threshold: {
    title: 'AI cost threshold reached',
    body: ({ percentUsed }) => `AI credit usage has reached ${percentUsed ?? 'the configured'} threshold${percentUsed == null ? '' : '%'}.`,
  },
  webhook_delivery_failed: {
    title: 'Webhook delivery failed',
    body: () => 'A webhook delivery could not be completed after all retry attempts.',
  },
};

const getEvent = (item) => item?.NotificationEvent || item?.notificationEvent || item?.event || {};

const getValue = (item, key) => item?.[key] ?? getEvent(item)?.[key];

export const getNotificationData = (item) => {
  const payload = getValue(item, 'payload') || {};
  const eventKey = getValue(item, 'event_key') || getValue(item, 'eventKey') || '';
  const recipientUserId = getValue(item, 'user_id') || getValue(item, 'userId');
  const copy = EVENT_COPY[eventKey];
  const resolvedTitle = typeof copy?.title === 'function' ? copy.title(payload, recipientUserId) : copy?.title;
  const resolvedBody = typeof copy?.body === 'function' ? copy.body(payload, recipientUserId) : copy?.body;

  return {
    eventKey,
    payload,
    projectId: getValue(item, 'project_id') || getValue(item, 'projectId'),
    title: payload.title || payload.subject || resolvedTitle || NOTIFICATION_PANEL_LABELS.unknownTitle,
    body: payload.body || payload.message || resolvedBody || eventKey.replaceAll('_', ' ') || '',
  };
};

export const getNotificationRoute = (item) => {
  const { eventKey, payload, projectId } = getNotificationData(item);
  const recipientUserId = getValue(item, 'user_id') || getValue(item, 'userId');
  const projectsRoot = `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}`;
  const projectPath = projectId ? `${projectsRoot}/${projectId}` : projectsRoot;

  if (payload.ctaUrl) return payload.ctaUrl;

  switch (eventKey) {
    case 'project_member_added':
      return projectId ? `${projectPath}/users` : projectsRoot;
    case 'project_member_removed':
      if (payload.removedUserId && payload.removedUserId === recipientUserId) {
        return projectsRoot;
      }
      return projectId ? `${projectPath}/users` : projectsRoot;
    case 'test_launch_finished':
      if (projectId && payload.testGroupId && payload.launchId) {
        return `${projectPath}${DEFAULT_ROUTES.TEST_GROUPS}/${payload.testGroupId}/launch-details/${payload.launchId}`;
      }
      return projectId ? `${projectPath}${DEFAULT_ROUTES.TEST_GROUPS}` : projectsRoot;
    case 'webhook_delivery_failed':
      return projectId
        ? `${projectPath}${DEFAULT_ROUTES.PROJECT_SETTINGS}`
        : projectsRoot;
    default:
      return projectPath;
  }
};

export const getNotificationTimestamp = (item) => item?.created_at || item?.createdAt || getEvent(item)?.created_at || getEvent(item)?.createdAt;
